
import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { Review } from '../types';
import { supabase } from '@/integrations/supabase/client';

// This store now only serves as a temporary cache for review data
// All data will be persisted to Supabase with proper RLS security
export const useReviewStore = create<{
  reviews: Review[];
  addReview: (reviewData: Partial<Review>) => Promise<Review | null>;
  fetchReviews: () => Promise<Review[]>;
}>((set, get) => ({
  reviews: [],
  
  // Modified to save reviews to Supabase with proper authentication checks
  addReview: async (reviewData) => {
    try {
      const newReview = {
        id: uuidv4(),
        createdAt: Date.now(),
        ...reviewData
      } as Review;
      
      // Get current user for proper attribution
      const { data: { user } } = await supabase.auth.getUser();
      
      // Save to Supabase with user_id if authenticated
      const { data, error } = await supabase
        .from('reviews')
        .insert({
          id: newReview.id,
          company: newReview.company,
          job_title: newReview.jobTitle,
          reason_for_exit: newReview.reasonForExit,
          location: newReview.location,
          exit_date: newReview.exitDate ? newReview.exitDate.toISOString() : null,
          fair_work_involved: newReview.fairWorkInvolved,
          positive_experience: newReview.positiveExperience,
          job_category: newReview.jobCategory,
          tenure: newReview.tenure,
          salary: newReview.salary,
          user_id: user?.id || null, // Properly set user_id for RLS
          created_at: new Date().toISOString(),
        })
        .select()
        .single();
        
      if (error) {
        console.error("Error saving review to Supabase:", error);
        throw new Error(`Failed to save review: ${error.message}`);
      }
      
      // Update local cache only after successful database save
      set((state) => ({
        reviews: [...state.reviews, newReview]
      }));
      
      return newReview;
    } catch (error) {
      console.error("Error in addReview:", error);
      return null;
    }
  },
  
  // Fetch reviews respecting RLS policies
  fetchReviews: async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching reviews from Supabase:", error);
        // Return cached data on error
        return get().reviews;
      }
      
      // Convert Supabase data format to our app's Review format
      const formattedReviews: Review[] = (data || []).map(review => ({
        id: review.id,
        company: review.company,
        jobTitle: review.job_title,
        reasonForExit: review.reason_for_exit,
        location: review.location,
        exitDate: review.exit_date ? new Date(review.exit_date) : undefined,
        fairWorkInvolved: review.fair_work_involved,
        positiveExperience: review.positive_experience,
        createdAt: new Date(review.created_at).getTime(),
        jobCategory: review.job_category || '',
        tenure: review.tenure,
        salary: review.salary,
        isHidden: review.is_hidden,
        unique_ref: review.unique_ref
      }));
      
      // Update the store with fetched data
      set({ reviews: formattedReviews });
      
      return formattedReviews;
    } catch (error) {
      console.error("Error in fetchReviews:", error);
      return get().reviews;
    }
  }
}));
